// Type-only, no cycle: ratingTaxonomy is import-free. The dependency runs
// against the usual ai-mode ← proposal-standard direction, but duplicating the
// shape here proved worse (silent drift risk between the two declarations).
import type { SpaceRating } from "../proposal-standard/components/ratingTaxonomy";

export interface DiagnosisData {
  dealId: string;
  companyName: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  /** HubSpot company `numberofemployees`; null when the deal has no company data. */
  numberOfEmployees?: number | null;
  segment: string;
  targetCity: string;
  selectedLocation?: { label: string; lat: number; lng: number } | null;
  importedData?: ImportDealResponse | null;
  dealInsights?: string[];
  positionsMin: number | "";
  positionsMax: number | "";
  monthlyBudget: string;
  needs: string;
  includeUnavailableWithinPeriod?: number;
}

export interface LocationData {
  location: { label: string; lat: number; lng: number } | null;
  radiusKm: number | null;
  capacityMin?: number | null;
  capacityMax?: number | null;
  creditsPrice?: number | null;
  /** Rooms picked individually (standalone), independent of combos. */
  selectedSpaceIds: string[];
  /** `combinationId`s of the combos the executive selected. */
  selectedComboIds?: string[];
  rankedSpaces?: RankedSpace[];
  extraSpaces?: RankedSpace[];
  idAddedSpaces?: RankedSpace[];
  roomCombinations?: import("@/features/vision-360/ai-mode/hooks/useSearchAndRank").RoomCombination[];
  recommendationHash?: string | null;
  searchSource?: SearchSource;
  locationMode?: "location" | "neighborhoods";
  cityName?: string;
  neighborhoodNames?: string[];
  includeUnavailableWithinPeriod?: number;
  toleranceCapacity?: number | null;
  toleranceCapacityIgnored?: boolean;
  tolerancePrice?: number | null;
  tolerancePriceIgnored?: boolean;
  toleranceDistance?: number | null;
  toleranceDistanceIgnored?: boolean;
  toleranceDistanceKm?: number | null;
  priceMin?: number | null;
  priceMax?: number | null;
}

export type SearchSource = "search-and-rank" | "filters" | "search";

export interface NewSearchTolerance {
  capacity?: number;
  price?: number;
  distance?: number;
}

export const CONTRACT_TERM_OPTIONS = [6, 12, 24, 36] as const;
export type ContractTerm = (typeof CONTRACT_TERM_OPTIONS)[number];
export const DEFAULT_CONTRACT_TERM: ContractTerm = 12;

export interface SpaceAdjustment {
  spaceId: string;
  extraValue: number;
  margin: number;
  clientPrice: number;
  credits: number;
  extraCredits: number;
  totalCredits: number;
  totalPrice: number;
  setupCost?: number;
  setupCredits?: number;
  marketDiscountPct?: number;
}

export type LicenseFeeMode = "percentage" | "fixed";

export const PROPOSAL_TYPE_OPTIONS = ["ENT", "SMB"] as const;
export type ProposalType = (typeof PROPOSAL_TYPE_OPTIONS)[number];
export const DEFAULT_PROPOSAL_TYPE: ProposalType = "ENT";

export const LICENSE_FEE_PERIOD_OPTIONS = ["semiannual", "annual"] as const;
export type LicenseFeePeriod = (typeof LICENSE_FEE_PERIOD_OPTIONS)[number];

export const DEFAULT_LICENSE_FEE_MODE: LicenseFeeMode = "percentage";
export const DEFAULT_LICENSE_FEE_PERCENTAGE = 8;

export interface AdjustmentsData {
  creditsPrice: number;
  contractMonths: ContractTerm | null;
  spaceAdjustments: SpaceAdjustment[];
  orderedSpaceIds?: string[];
  proposalValidity: number;
  sellerNotes: string;
  recommendedSpaceIds?: string[];
  selectedSpaces?: RankedSpace[];
  includeFlexCredits?: boolean;
  flexCredits?: number;
  showLicenseFee?: boolean;
  licenseFeeMode?: LicenseFeeMode;
  licenseFeePercentage?: number;
  licenseFeeFixed?: number;
  licenseFeeValue?: number;
  licenseFeePeriod?: LicenseFeePeriod;
  includeSetupFee?: boolean;
  setupFee?: number;
  proposalType?: ProposalType;
}

export interface AIModeState {
  currentStep: number;
  completedSteps: number[];
  diagnosis: DiagnosisData | null;
  location: LocationData | null;
  adjustments: AdjustmentsData | null;
  /**
   * "quick" = busca rápida (PED-12650): LocationStep only, view-only, data
   * never persisted to deal/proposal flows. Absent/"full" = normal wizard.
   */
  mode?: "full" | "quick";
}

export interface ActiveCombinationPreview {
  combinationId: string;
  lat: number;
  lng: number;
  rooms: Array<{
    id: string;
    name: string;
    images: string[];
  }>;
}

export interface LivePreview {
  companyName: string;
  targetCity: string;
  positionsMin: number | "";
  positionsMax: number | "";
  dealImported: boolean;
  dealInsights?: string[];
  location: { label: string; lat: number; lng: number } | null;
  radiusKm: number | null;
  activeSpaceId: string | null;
  activeCombination?: ActiveCombinationPreview | null;
  rankedSpaces?: RankedSpace[];
  extraSpaces?: RankedSpace[];
  showOtherSpaces?: boolean;
  outdatedSpaces?: RankedSpace[];
  showOutdatedSpaces?: boolean;
  selectedSpaceIds?: string[];
}

export interface ProposalCreatedBy {
  userId: number | null;
  email: string | null;
  fullName: string | null;
}

export type ProposalUpdatedBy = ProposalCreatedBy;

export interface ProposalVersionDiff {
  from: unknown;
  to: unknown;
}

export interface ProposalVersion {
  versionNumber: number;
  snapshotAt: string;
  updatedBy: ProposalUpdatedBy | null;
  snapshot: Record<string, unknown>;
  diffs: Record<string, ProposalVersionDiff>;
}

export type SpaceStaleStatus = "unavailable" | "data-changed";

export interface FieldDiff {
  field: string;
  label: string;
  previous: string;
  current: string;
}

export interface SpaceDiff {
  spaceId: string;
  fields: FieldDiff[];
  freshSpace: RankedSpace;
}

export interface RankedSpaceWithStaleInfo extends RankedSpace {
  staleStatus?: SpaceStaleStatus;
  previousData?: Pick<
    RankedSpace,
    "pricePerWorkstation" | "monthlyPrice" | "capacity"
  >;
}

export interface ProposalPayload {
  uuid: string;
  state: AIModeState;
  generatedAt: string;
  suggestedSpaces?: RankedSpace[];
  proposalType?: "ENT" | "SMB";
  proposalOrigin?: string;
  createdBy?: ProposalCreatedBy;
  versionNumber?: number;
  updatedBy?: ProposalUpdatedBy;
  updatedAt?: string;
  versions?: ProposalVersion[];
  /** Current client thumbs ratings by space id, hydrated from the GET.
   * Visit-only entries (reasons without a thumbs value) carry no UI state
   * and are not mapped. */
  spaceRatings?: Record<string, SpaceRating>;
}

export type UpdateProposalBody = Omit<CreateProposalBody, "created_by"> & {
  updated_by: {
    user_id: number | null;
    email: string | null;
    full_name: string | null;
  };
  expected_version?: number;
};

export interface UpdateProposalResult {
  proposal?: ProposalPayload;
  noChanges: boolean;
}

export interface RankedSpace {
  id: string;
  searchId?: number | null;
  partnerId?: number;
  spaceName: string;
  roomName: string;
  city: string;
  address: string;
  lat: number;
  lng: number;
  pricePerWorkstation: number;
  monthlyPrice: number | null;
  credits: number | null;
  capacity: number;
  sizeM2: number | null;
  score: number;
  amenities: string[];
  spaceAmenities?: string[];
  /**
   * Compliance documents of the BUILDING (never of the room): AVCB (fire
   * department inspection certificate) and the operating permit. Booleans,
   * not the file URLs — the client-facing proposal only ever shows that the
   * document exists and must never be able to open it (PED-12757).
   *
   * Absent/undefined means "we don't know", not "the building lacks it", so
   * nothing is rendered for it — never a negative signal.
   */
  spaceHasAvcb?: boolean;
  spaceHasOperatingPermit?: boolean;
  imageUrl: string;
  images: string[];
  origin?: string | null;
  source?: string | null;
  neighborhood?: string;
  isRecommended?: boolean;
  addedById?: boolean;
  addedByIdValue?: string;
  salesPitch?: string;
  matchReasons?: string[];
  detailedScoreAnalysis?: Array<{
    criterion: string;
    requested: string;
    offered: string;
    matched: boolean;
  }>;
  scoreDetails?: import("@/features/vision-360/types").ScoreDetail | null;
  isOutdated?: boolean;
  daysSinceUpdate?: number;
  availabilityDate?: string | null;
  inToleranceZone?: boolean;
  spacePhotos?: string[];
  /**
   * Curated subset of `images` (room photos) chosen for the proposal, in display
   * order. Array order = display order, membership = selection. `undefined` means
   * "not curated yet" → treat as all `images` selected.
   */
  selectedImages?: string[];
  /** Curated subset of `spacePhotos` (building photos) for the proposal, in order. */
  selectedSpacePhotos?: string[];
  /**
   * `combinationId`s of every combo this room belongs to. A room can be part of
   * more than one combination, so it renders inside each one. Empty/undefined =
   * not part of any combo ("avulsa").
   */
  groupIds?: string[];
  /**
   * True when this room was picked individually (its own standalone card),
   * independent of any combo. A room can be both `isIndividual` AND have
   * `groupIds` — it then renders as a standalone AND inside its combo(s).
   */
  isIndividual?: boolean;
  scoreBreakdown?: Array<{
    label: string;
    score: number;
    inTolerance: boolean;
    detail?: string;
  }>;
}

/**
 * Canonical keys for form fields a HubSpot import could not fill.
 * Sent verbatim to analytics (missing_fields) — keep them stable English
 * identifiers; the Portuguese labels live in HubSpotImportCard.
 */
export type ImportMissingField =
  | "company"
  | "city"
  | "capacity"
  | "contact_email"
  | "contact_name"
  | "segment";

/**
 * Lifecycle of the backend's AI extraction for an imported deal.
 * "pending" — fast deterministic data returned, LLM still running in
 * background; "ready" — full GoldenBrief available; "failed" — extraction
 * died (re-import to retry).
 */
export type ExtractionStatus = "pending" | "ready" | "failed";

export interface ImportDealResponse {
  /**
   * Absent on backend versions that still extract synchronously — treat
   * undefined as "ready".
   */
  extraction_status?: ExtractionStatus;
  company_name: string | null;
  industry?: string | null;
  primary_contact_email?: string | null;
  primary_contact_name?: string | null;
  /**
   * HubSpot company `numberofemployees`; null when the deal has no company
   * or the property is empty. Absent on backend versions without the field.
   */
  company_size?: number | null;
  confidence: number;
  summary_narrative: string;
  deal_insights?: string[];
  hard_constraints: {
    city: string;
    min_capacity: number | null;
    max_capacity: number | null;
    min_budget?: number | null;
    max_budget: number | null;
    mandatory_amenities: string[];
    latitude?: number | null;
    longitude?: number | null;
    radius_km?: number | null;
    unit_credit_price?: number | null;
  };
  deal_context?: {
    deal_allocated_budget: string;
  };
  soft_preferences: {
    preferred_neighborhoods: string[];
    nice_to_have_amenities: string[];
    mobility_needs: string[];
    infrastructure_needs: string[];
  };
  cultural_fit: {
    company_vibe: string;
    work_model: string | null;
    deal_breakers: string[];
  };
}

/** Response of GET /import-deal/{deal_id}/brief (extraction polling). */
export interface ImportDealBriefStatus {
  status: ExtractionStatus;
  brief: ImportDealResponse | null;
}

export interface DealContext {
  deal_id: string;
  deal_name: string;
  deal_amount: number;
  deal_city: string;
  deal_state: string;
  deal_neighborhood: string;
  deal_positions_demand: string;
  deal_rooms_quantity: string;
  deal_allocated_budget: string;
  company_name: string;
  company_industry: string;
  company_size: number;
  /**
   * Same headcount under the raw HubSpot property name — this is the key the
   * persisted proposal is read back through (GetProposalApiResponseV2 →
   * diagnosis.numberOfEmployees). `company_size` above mirrors the backend's
   * RawDealContext shape for search-and-rank; keep both in sync.
   */
  numberofemployees?: number | null;
  company_domain: string;
  contact_name: string;
  contact_email: string;
  contact_title: string;
  engagements: Array<{
    type: string;
    timestamp: string;
    body: string;
    subject: string;
    direction: string;
  }>;
}

export type SearchAndRankBody = ImportDealResponse & {
  deal_context: DealContext;
  unit_credit_price?: number | null;
  current_monthly_cost?: number | null;
};

export type CreateProposalBody = SearchAndRankBody & {
  proposal_type: "ENT" | "SMB";
  proposal_origin?: string;
  created_by?: {
    user_id: number | null;
    email: string | null;
    full_name: string | null;
  };
  recommendation_hash: string | null;
  location_label: string | null;
  location_lat: number | null;
  location_lng: number | null;
  radius_km: number | null;
  selected_office_ids: string[];
  recommended_space_ids: string[];
  credits_price: number;
  contract_months: number;
  proposal_validity: string;
  seller_notes: string;
  space_adjustments: Array<{
    space_id: string;
    extra_value: number;
    margin: number;
    client_price: number;
    credits: number;
    extra_credits: number;
    total_credits: number;
    total_price: number;
    setup_cost: number;
    setup_credits?: number;
    market_discount_pct?: number;
  }>;
  selected_spaces: RankedSpace[];
  include_flex_credits?: boolean;
  flex_credits?: number;
  show_license_fee?: boolean;
  // License Fee Global
  license_fee_mode?: LicenseFeeMode;
  license_fee_percentage?: number;
  license_fee_fixed?: number;
  license_fee_value?: number;
  license_fee_period?: LicenseFeePeriod;
  // Setup Fee (exclusivo ENT): pago uma única vez, fora do custo mensal
  include_setup_fee?: boolean;
  setup_fee?: number;
};

export interface CreateProposalResponse {
  uuid: string;
  proposalId: string;
  proposalUrl: string;
  proposal_type?: "ENT" | "SMB" | string;
  status: string;
  createdAt: string;
  expiresAt: string;
  recommendationHash: string;
  rooms: unknown[];
}

// POI types are map-domain and live with the map layer. Re-exported here so
// historical import sites keep working. Deep path on purpose: the map barrel
// would drag react-map-gl into every module importing this file.
export type {
  POICategory,
  PointOfInterest,
} from "@/features/vision-360/map/types";

export type GetProposalResponse = CreateProposalBody;

/** One document of the `proposal_space_ratings` collection as hydrated into
 * GET /api/v1/proposals/{uuid} (`space_ratings`). Wire shape is snake_case. */
export interface SpaceRatingApiEntry {
  space_id: string;
  rating?: "up" | "down" | null;
  reasons?: string[];
  sub_reasons?: string[];
  comment?: string | null;
  /** Self-reported e-mail from the proposal email gate; absent on ratings
   * created before the field existed. */
  rated_by_email?: string | null;
  visit_reasons?: string[];
  visit_comment?: string | null;
}

export interface GetProposalApiResponseV2 {
  space_ratings?: SpaceRatingApiEntry[];
  uuid: string;
  proposalId?: string;
  proposalUrl?: string;
  status: string;
  createdAt: string | { $date: string };
  recommendationHash?: string | null;
  expiresAt: number | string;
  proposal_validity?: number | string;
  version_number?: number;
  updated_by?: {
    user_id: number | null;
    email: string | null;
    full_name: string | null;
  } | null;
  updated_at?: string | null;
  versions?: Array<{
    version_number: number;
    snapshot_at: string;
    updated_by: {
      user_id: number | null;
      email: string | null;
      full_name: string | null;
    } | null;
    snapshot: Record<string, unknown>;
    diffs: Record<string, { from: unknown; to: unknown }>;
  }>;
  company_name?: string;
  hard_constraints: {
    city: string;
    capacity_min?: number | null;
    capacity_max?: number | null;
    budget_max?: number | null;
    min_capacity?: number | null;
    max_capacity?: number | null;
    max_budget?: number | null;
  };
  deal_context: {
    deal_id: string;
    company_name: string;
    contact_name?: string;
    contact_email?: string;
    contact_phone?: string;
    segment?: string;
    numberofemployees?: string | number | null;
    [key: string]: unknown;
  };
  soft_preferences?: {
    preferred_neighborhoods?: string | string[];
    [key: string]: unknown;
  };
  cultural_fit?: { [key: string]: unknown };
  summary_narrative?: string;
  location_label?: string | null;
  location_lat?: number | null;
  location_lng?: number | null;
  radius_km?: number | null;
  selected_office_ids?: string[];
  recommended_space_ids: string[];
  credits_price?: number;
  contract_months?: number;
  seller_notes?: string;
  proposal_type?: string;
  proposal_origin?: string;
  created_by?: {
    user_id: number | null;
    email: string | null;
    full_name: string | null;
  };
  include_flex_credits?: boolean;
  flex_credits?: number;
  show_license_fee?: boolean;
  license_fee_mode?: LicenseFeeMode;
  license_fee_percentage?: number;
  license_fee_fixed?: number;
  license_fee_value?: number;
  license_fee_period?: LicenseFeePeriod;
  include_setup_fee?: boolean;
  setup_fee?: number;
  space_adjustments: unknown[];
  selected_spaces: Array<{
    private_room_id?: string;
    id?: string;
    space_id?: string;
    space_name?: string;
    label: string;
    capacity: number;
    monthly_price: number;
    size?: number | null;
    neighborhood?: string;
    address?: string;
    cityname?: string;
    space_latitude?: string;
    space_longitude?: string;
    latitude?: string;
    longitude?: string;
    images?: string[];
    spacePhotos?: string[];
    selectedImages?: string[];
    selectedSpacePhotos?: string[];
    groupIds?: string[];
    isIndividual?: boolean;
    amenities?: string | null;
    relevance_score?: number;
    match_percent?: string | null;
    search_id?: number | null;
    creditsQuantity: number;
    clientPrice: number;
    setupPrice: number;
    setupCredits?: number;
    totalPriceFinal: number;
    market_discount_pct?: number;
    space_details?: {
      id?: number | string;
      name?: string;
      address?: string | null;
      neighborhood?: string | null;
      cityname?: string | null;
      latitude?: string | null;
      longitude?: string | null;
      amenities?: string | null;
    } | null;
    details?: {
      picture_url?: string | null;
      picture_url1?: string | null;
      picture_url2?: string | null;
      picture_url3?: string | null;
      picture_url4?: string | null;
      picture_url5?: string | null;
      origin?: string | null;
      source?: string | null;
    } | null;
    availabilityDate?: string | null;
  }>;
}

export interface GetProposalApiResponse {
  uuid: string;
  proposalId: string;
  proposalUrl: string;
  status: string;
  createdAt: string;
  expiresAt: string;
  recommendationHash: string | null;
  proposal_settings: {
    validity_days: number;
    contract_months: number;
    woba_margin: number;
    unit_credit_price: number;
    deal_allocated_budget: number | null;
  };
  deal_context: {
    deal_id: string;
    deal_name: string;
    deal_amount: number;
    deal_insights: string[];
  };
  company_name: string;
  hard_constraints: {
    city: string;
    credit_value: number | null;
    latitude: number | null;
    longitude: number | null;
    mandatory_amenities: string[];
    max_budget: number | null;
    max_capacity: number | null;
    min_budget: number | null;
    min_capacity: number | null;
    radius_km: number | null;
  };
  soft_preferences: {
    infrastructure_needs: string[];
    mobility_needs: string[];
    nice_to_have_amenities: string[];
    preferred_neighborhoods: string[];
  };
  cultural_fit: {
    company_vibe: string;
    deal_breakers: string[];
    work_model: string | null;
  };
  summary_narrative: string;
  selected_spaces: Array<{
    id: string;
    name: string;
    capacity: number;
    monthly_price: number;
    neighborhood: string;
    city: string;
    size_m2: number;
    amenities: string[];
    photos: string[];
    match_score: number;
    credits_price: number;
    credits: number;
  }>;
  price_adjustments: Array<{
    space_id: string;
    monthly_price: number;
    credits_price: number;
    credits: number;
    total_price: number;
  }>;
  recommendations: {
    recommendation_hash: string | null;
    top_picks: Array<{
      office_id: string;
      match_score: number;
      sales_pitch: string;
      match_reasons: string[];
      source: string;
      origin: string;
    }>;
    top_picks_details: Array<{
      id: string;
      search_id: number;
      name: string;
      capacity: number;
      monthly_price: number;
      neighborhood: string;
      city: string;
      latitude: number;
      longitude: number;
      amenities: string[];
      photos: string[];
      similarity_score: number;
      availability_tier: string;
      match_type: string;
      partner_id: string;
      space_name: string;
      freshness_days: number;
      status_updated_at: string | null;
      price_updated_at: string | null;
      source: string;
      origin: string;
      credits: number | null;
      distance_km: number | null;
      size_m2: number;
    }>;
    other_active: Array<Record<string, unknown>>;
    reactivation_leads?: Array<Record<string, unknown>>;
  } | null;
}
